import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { ISelectOption } from 'components/forms/Selects'
import { camelize } from 'humps'
import omit from 'lodash/omit'
import { Facet } from 'transfix-ui/components/Facet'
import {
  FilterMultiSelect,
  IFilterMultiSelectOption,
} from 'transfix-ui/components/FilterMultiSelect'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { SingleSelect } from 'transfix-ui/components/SingleSelect'
import { STOP_TYPE } from 'types/shipment'
import useUserDetails from 'utils/hooks/useUserDetails'
import {
  filterMileOptions,
  sortByOptions,
  defaultSelection,
} from 'apps/booking/constants/carrierList'
import {
  ICarrierMatchesFilters,
  MatchTypeOptions as defaultMatchTypeOptions,
  MatchStrategyValues,
  SortOrder,
  SortField,
} from 'apps/booking/types/carrierMatches'
import { matchesRequestStateSelector } from 'apps/booking/utils/selectors/carrierMatches'
import { activeRowIdSelector } from 'apps/booking/utils/selectors/sidebar'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { UserIcon, StarIcon, SortArrowDownIcon } from 'transfix-icons'
import { lookupStateName } from 'utils/getLocations'

interface IFilterProps {
  filters: Partial<ICarrierMatchesFilters>
  setFilters: (updatedFilters: Partial<ICarrierMatchesFilters>) => void
  pickup?: string
  delivery?: string
}

export const Filters = memo<IFilterProps>(({ delivery, filters, pickup, setFilters }) => {
  const { trackEvent } = useMPAnalytics()
  const { userDetails } = useUserDetails()
  const { loading } = useSelector(matchesRequestStateSelector)
  const { activeRowId } = useSelector(activeRowIdSelector)
  const shipmentId = String(activeRowId)
  const {
    bookedWithIds,
    deliveryStateCode,
    destinationRadiusMiles,
    filteredStrategies,
    originRadiusMiles,
    pickupStateCode,
    sortField,
  } = filters
  const { id: currentUserId = '' } = userDetails || {}

  const withoutPostedTrucks = (strategy: MatchStrategyValues) =>
    strategy !== MatchStrategyValues.postedTruck
  const matchTypeStrategies = filteredStrategies?.filter(withoutPostedTrucks)

  const isBookedWithMeSelected = Boolean(bookedWithIds && bookedWithIds.length > 0)

  const isSelected = ({ selected }: IFilterMultiSelectOption) => selected
  const matchTypeValue = ({ value }: IFilterMultiSelectOption) => value as MatchStrategyValues
  const isMatchTypeStrategiesSelected = Boolean(
    matchTypeStrategies && matchTypeStrategies.length > 0
  )

  const [matchTypeOptions, setMatchTypeOptions] = useState<IFilterMultiSelectOption[]>(
    defaultMatchTypeOptions
  )

  const defaultSort = sortByOptions.find(defaultSelection)
  const [selectedSortByOption, setSelectedSortByOption] = useState<ISelectOption | undefined>(
    defaultSort
  )

  // Sort By Handler

  const sortByHandler = (sortByOption?: ISelectOption) => {
    const { value } = sortByOption || {}
    trackEvent('Booking', 'UPDATED_SORT_BY_FILTER', {
      shipmentId,
      sortField: camelize(String(value)),
    })
    setSelectedSortByOption(sortByOption)
    if (sortField && !value) {
      setFilters(omit(filters, ['sortField', 'sortOrder']))
    } else if (value) {
      const fieldValue = value as SortField
      const sortOrder = fieldValue === SortField.lastRate ? SortOrder.asc : SortOrder.desc
      setFilters({ ...filters, sortField: fieldValue, sortOrder })
    }
  }

  // Booked with Me Handler

  const bookedWithMeHandler = () => {
    trackEvent('Booking', 'UPDATED_BOOKED_WITH_ME_FILTER', {
      shipmentId,
      bookedWithMe: String(!isBookedWithMeSelected),
    })
    if (isBookedWithMeSelected) {
      setFilters(omit(filters, ['bookedWithIds']))
    } else {
      setFilters({ ...filters, bookedWithIds: currentUserId.toString() })
    }
  }

  // Match Type Handlers (Multi-Select)
  const onSelectMatchTypeHandler = (selectedOptions?: IFilterMultiSelectOption[]) => {
    // Converts selected options into a key/value to determine if a filter is selected or not
    const trackOptions =
      selectedOptions
        ?.map(s => ({ [camelize(s.value)]: s.selected }))
        .reduce((acc, val) => ({ ...acc, ...val }), {}) || {}
    trackEvent('Booking', 'UPDATED_MATCH_TYPE_FILTER', {
      shipmentId,
      ...trackOptions,
    })
    if (selectedOptions) {
      const hasOptionsSelected = selectedOptions.some(isSelected)
      const selectedMatchTypes = selectedOptions.filter(isSelected).map(matchTypeValue)

      if (hasOptionsSelected) {
        setFilters({ ...filters, filteredStrategies: selectedMatchTypes })
      } else {
        setFilters({
          ...filters,
          filteredStrategies: defaultMatchTypeOptions.map(matchTypeValue),
        })
      }
      setMatchTypeOptions(selectedOptions)
    }
  }

  // Pickup/Delivery Radius Handlers

  const radiusHandler = (radiusType: STOP_TYPE, value?: string) => {
    const isPickupRadiusType = radiusType === STOP_TYPE.pickup
    const trackingMetadata = {
      shipmentId,
      value: String(value),
    }
    if (isPickupRadiusType) {
      trackEvent('Booking', 'UPDATED_PICKUP_RADIUS_FILTER', trackingMetadata)
    } else {
      trackEvent('Booking', 'UPDATED_DELIVERY_RADIUS_FILTER', trackingMetadata)
    }
    const stateOrMiles = isPickupRadiusType
      ? pickupStateCode || originRadiusMiles
      : deliveryStateCode || destinationRadiusMiles
    const mileKey = isPickupRadiusType ? 'originRadiusMiles' : 'destinationRadiusMiles'
    const stateKey = isPickupRadiusType ? 'pickupStateCode' : 'deliveryStateCode'

    if (stateOrMiles && !value) {
      setFilters(omit(filters, [mileKey, stateKey]))
    } else if (value && !Number.isNaN(Number.parseInt(value, 10))) {
      setFilters(omit({ ...filters, [mileKey]: Number(value) }, [stateKey]))
    } else if (value) {
      setFilters(omit({ ...filters, [stateKey]: value }, [mileKey]))
    }
  }

  const defaultMileage = filterMileOptions.find(defaultSelection)
  const pickupOptions = [...filterMileOptions]
  const [selectedPickupOption, setSelectedPickupOption] = useState<ISelectOption | undefined>(
    defaultMileage
  )
  if (pickup) {
    const pickupState = pickup.split(',')[1].trim()
    pickupOptions.push({
      label: `${lookupStateName(pickupState)}`,
      value: pickupState,
    })
  }
  const pickupRadiusHandler = (selectedRadius?: ISelectOption) => {
    setSelectedPickupOption(selectedRadius)
    const { value } = selectedRadius || {}
    radiusHandler(STOP_TYPE.pickup, value as string)
  }

  const deliveryOptions = [...filterMileOptions]
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<ISelectOption | undefined>(
    defaultMileage
  )
  if (delivery) {
    const deliveryState = delivery.split(',')[1].trim()
    deliveryOptions.push({
      label: `${lookupStateName(deliveryState)}`,
      value: deliveryState,
    })
  }
  const destinationRadiusHandler = (selectedRadius?: ISelectOption) => {
    setSelectedDeliveryOption(selectedRadius)
    const { value } = selectedRadius || {}
    radiusHandler(STOP_TYPE.delivery, value as string)
  }

  return (
    <div>
      <SingleSelect
        id='sort-by'
        dataTest='sort-by'
        onSelect={sortByHandler}
        label='Sort By'
        icon={<SortArrowDownIcon size='large' />}
        options={sortByOptions}
        className='mr2 mt2'
        selected={selectedSortByOption}
      />
      <SingleSelect
        id='pickup-radius'
        dataTest='pickup-radius'
        onSelect={pickupRadiusHandler}
        label='Pickup Radius'
        icon={<MapMarker type={STOP_TYPE.pickup} sequence={0} className='icon large fl' />}
        options={pickupOptions}
        className='mr2 mt2'
        selected={selectedPickupOption}
      />
      <SingleSelect
        id='delivery-radius'
        dataTest='delivery-radius'
        onSelect={destinationRadiusHandler}
        label='Delivery Radius'
        icon={<MapMarker type={STOP_TYPE.delivery} sequence={0} className='icon large fl' />}
        options={deliveryOptions}
        className='mr2 mt2'
        selected={selectedDeliveryOption}
      />
      <FilterMultiSelect
        id='match-type'
        onSelect={onSelectMatchTypeHandler}
        label='Match Type'
        icon={<StarIcon size='large' />}
        dataTest='match-type-multi-select'
        options={matchTypeOptions}
        loading={isMatchTypeStrategiesSelected && loading}
        className='mr2 mt2'
      />
      <Facet
        id='bookedWithMe'
        dataTest='booked-with-me-facet'
        onClick={bookedWithMeHandler}
        label='Booked With Me'
        icon={<UserIcon size='large' />}
        selected={isBookedWithMeSelected}
        isLoading={isBookedWithMeSelected && loading}
        className='dib mr2 mt2'
      />
    </div>
  )
})
